import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Col,
  Container,
  Row,
  CardBody,
  Card,
  TabPane,
  TabContent,
} from 'reactstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import AlertOutlineIcon from 'mdi-react/AlertOutlineIcon';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import InfoBox from '../../shared/components/custom/InfoBox';
import Loading from '../../shared/components/custom/Loading';
import SpotlightGroupForm from './components/SpotlightGroupForm';
import t, { partial } from '../../util/translation/translation';
import NavTab from '../../shared/components/custom/NavTab';
import TileListPage from './TileListPage';

const SpotlightGroupFormPage = () => {
  const history = useHistory();
  const [entity, setEntity] = useState(null);
  const [groupTitle, setGroupTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  // Whether the saved takeover is on the app's Home screen (null = unknown)
  const [onHome, setOnHome] = useState(null);
  const { entityID } = useParams();
  const config = useConfig();
  const formFormat = useSelector(
    (state) => getFormValues('spotlightGroupForm')(state)?.format
  );
  const p = partial('HighlightBannerFormPage');
  const s = partial('shared');
  const bp = partial('HighlightBannerPage');
  const getData = async () => {
    setLoading(true);
    try {
      const groupData = await Api.spotlight.getGroupById(entityID);
      setEntity(groupData);
      setGroupTitle(groupData?.title);
      setLoading(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityID) {
      getData();
    }
  }, [entityID]);
  const savedTakeover = entity?.format === 'TAKEOVER';
  // A takeover only opens when its group is in the Home layout (as
  // spotlight-<id>); if the layout can't be read, no warning
  useEffect(() => {
    if (!savedTakeover || !entityID || !config?.id) {
      setOnHome(null);
      return undefined;
    }
    let cancelled = false;
    Api.app_layout.app
      .all(config.id)
      .then(({ data }) => {
        if (!cancelled) {
          setOnHome(
            (data?.home || []).some(
              (item) => item?.value === `spotlight-${entityID}`
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setOnHome(null);
      });
    return () => {
      cancelled = true;
    };
  }, [savedTakeover, entityID, config?.id]);
  const onSubmit = async (values) => {
    const isTakeover = values.format === 'TAKEOVER';
    const payload = {
      title: values.title,
      format: values.format || 'CIRCLE',
      scrollable: true,
      // A takeover always fills the screen
      size: isTakeover ? 'FULL_SCREEN' : values.size,
      // Text banners and story slides always show their text; a takeover
      // shows no description
      descriptionAvailable:
        values?.format === 'TEXT_BANNER' || values?.format === 'STORY'
          ? true
          : !isTakeover && String(values.descriptionAvailable) === 'true',
      titleVisible: !values.hideTitle,
      subTitle: values.subTitle || '',
      customColors: !!values.customColors,
      // The group's own colours (used with custom colours); empty = none
      backgroundColor: values.backgroundColor || null,
      textColor: values.textColor || null,
      accentColor: values.accentColor || null,
      descriptionBackgroundColor: values.descriptionBackgroundColor || null,
      descriptionTextColor: values.descriptionTextColor || null,
      textPosition:
        !isTakeover && values.textPosition === 'OVERLAY' ? 'OVERLAY' : null,
      // How often a takeover opens by itself; none for other formats
      takeoverFrequency: isTakeover ? values.takeoverFrequency || 'ONCE' : null,
      // Extra wait after Home has loaded; empty = none
      takeoverDelaySeconds:
        isTakeover && `${values.takeoverDelaySeconds ?? ''}`.trim() !== ''
          ? Number(values.takeoverDelaySeconds)
          : null,
      // Removed = null; unchanged = its url; new = the cropped image
      backgroundImage: values.backgroundImage?.remove
        ? null
        : values.backgroundImage?.croppedBase64 ||
          values.backgroundImage?.preview ||
          null,
      image:
        values.format === 'BANNER' ||
        values.format === 'TEXT_BANNER' ||
        (!values?.headingImage?.croppedBase64 && !values?.headingImage?.preview)
          ? null
          : values.headingImage.croppedBase64 || values.headingImage.preview,
    };

    setLoading(true);
    try {
      let resp;
      if (entityID) {
        await Api.spotlight.updateGroup(entityID, payload);
        // Saving stays on this page without refetching, so keep the
        // heading in step with a renamed group. Kept apart from entity,
        // since changing entity re-initializes the form with old values.
        setGroupTitle(values.title);
        toast.success(p('slideUpdated'));
      } else {
        resp = await Api.spotlight.createGroup(payload);
        toast.success(p('slideCreated'));
      }
      setLoading(false);
      history.replace(
        `/spotlight/group/edit/${entityID || resp.data.id}?tab=2`
      );
    } catch (err) {
      if (err?.data?.message === 'group.groupExists') {
        toast.error(t('ErrorMessage.nameAlereadyExists'));
      } else {
        toast.error(err || s('somethingWentWrong'));
      }
      setLoading(false);
    }
  };
  return (
    <Container>
      <Loading loading={loading} />
      <Row>
        <Col>
          <h3 className="page-title">{groupTitle || s('spotlight')}</h3>
        </Col>
      </Row>
      {(formFormat || entity?.format) === 'TAKEOVER' && (
        <Row>
          <Col>
            <InfoBox>
              <strong>{bp('takeoverNoticeTitle')}</strong>{' '}
              {bp('takeoverNoticeText')}
            </InfoBox>
            {savedTakeover && onHome === false && (
              <InfoBox icon={<AlertOutlineIcon size={18} />}>
                <strong>{bp('takeoverNotOnHomeTitle')}</strong>{' '}
                {bp('takeoverNotOnHomeText')}{' '}
                <Link to="/app_layout">{bp('openAppLayout')}</Link>
              </InfoBox>
            )}
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Card>
            <CardBody style={{ padding: 0, borderRadius: 6 }}>
              <NavTab
                tabs={[
                  { tab: s('details') },
                  { tab: p('tiles'), disabled: !entityID },
                ]}
                style={{ marginBottom: 0 }}
                setTab={setActiveTab}
                activeTab={activeTab}
                size="sm"
              />
              <div className="tabs tabs--justify tabs--bordered-top overflow-unset px-3 pb-3">
                <div className="tabs__wrap">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <SpotlightGroupForm entity={entity} onSubmit={onSubmit} />
                    </TabPane>
                    <TabPane tabId="2">
                      <TileListPage
                        groupId={entityID}
                        group={entity}
                        setLoading={setLoading}
                      />
                    </TabPane>
                  </TabContent>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SpotlightGroupFormPage;
